import { useRef } from "react";
import { m, useMotionValue, useSpring, useTransform } from "framer-motion";
import "../styles/tilt.css";

/**
 * Reusable 3D tilt card. Tracks the pointer and rotates the card in 3D space
 * with a smooth spring, plus a soft light "glare" that follows the cursor.
 * Respects prefers-reduced-motion (tilt simply stays flat).
 *
 * Usage: drop-in replacement for a card <div>.
 *   <TiltCard className="cat-card">...</TiltCard>
 */
const TiltCard = ({
  children,
  className = "",
  max = 9,
  scale = 1.04,
  glare = true,
  style,
  ...rest
}) => {
  const ref = useRef(null);

  // pointer position normalized to -0.5..0.5
  const px = useMotionValue(0);
  const py = useMotionValue(0);

  const springCfg = { stiffness: 220, damping: 18, mass: 0.4 };
  const sx = useSpring(px, springCfg);
  const sy = useSpring(py, springCfg);

  const rotateX = useTransform(sy, [-0.5, 0.5], [max, -max]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-max, max]);

  const glareX = useTransform(sx, [-0.5, 0.5], [0, 100]);
  const glareY = useTransform(sy, [-0.5, 0.5], [0, 100]);
  const glareBg = useTransform(
    [glareX, glareY],
    ([gx, gy]) =>
      `radial-gradient(circle at ${gx}% ${gy}%, rgba(255,255,255,0.45), rgba(255,255,255,0) 55%)`
  );

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width - 0.5);
    py.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleLeave = () => {
    px.set(0);
    py.set(0);
  };

  return (
    <m.div
      ref={ref}
      className={`tilt-card ${className}`}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      whileHover={{ scale }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      style={{
        rotateX,
        rotateY,
        transformPerspective: 900,
        ...style,
      }}
      {...rest}
    >
      {children}
      {glare && <m.span aria-hidden className="tilt-glare" style={{ background: glareBg }} />}
    </m.div>
  );
};

export default TiltCard;
